import { DatePicker, Divider, Input, Modal, Select, Spin, Tooltip } from "antd";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as ExcelJS from 'exceljs';
import TableFilter from "../../common/TableFilter/TableFilter";
import Button from "../../common/Button/Button";
import Breadcrumb from "../../common/Breadcrumb/Breadcrumb";
import GenericGrid from "../../common/Grid/GenericGrid";
import PDFStream from "../../common/PDFStream/PDFStream";
import filterColumns from "./filterColumns";
import { fetchSpecialOrders } from "./actions";
import { MRT_RowSelectionState } from "material-react-table";
import dayjs from "dayjs";
import Label from "src/components/common/Label/Label";
import { fetchVendorList } from "src/components/vendor/vendor-sagas";
import { getUserName } from "src/components/common/UserDetails";
import { DownloadOutlined } from "@ant-design/icons";
import {
  showErrorNotification,
  showSuccessNotification,
} from "src/util/notifications";
import {
  SPECIAL_ORDER_QUEUE_DATA_TYPE,
  SPECIAL_ORDER_QUEUE_EXCEL_COLUMNS,
} from "src/util/columns";
import useRolePermission from "src/hooks/useRolePermission";
import { printLabelReport, updateAcuityStatus, updatePrintLabel } from "../sagas";
import { reprintLabelsReport } from "../sagas";
import TextArea from "antd/es/input/TextArea";
import { convertStrToArr } from "src/util/utils";
import { set } from "lodash";
import api from "../../../util/api";
import { SPECIAL_ORDER_QUEUE_URL } from "../../../util/constants/api.constants";

function SpecialOrderQueue() {
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>([]);
  const [notFoundRow, setNotFoundRow] = useState<any>([]);
  const [vendors, setVendors] = useState<any>([]);
  const [vendorId, setVendorId] = useState<any>(undefined);
  const [backDays, setBackDays] = useState<any>([]);
  const [backDaysId, setBackDaysId] = useState<any>(undefined);
  const userName = getUserName();
  const [filteredData, setFilteredData] = useState<any>([]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [stream, setStream] = useState<string>("");
  const [reprintModal, setReprintModal] = useState(false);
  const [isScanUPCMode, setIsScanUPCMode] = useState(false); // NEW: distinguish modal mode
  const [orderList, setOrderList] = useState<any[]>([]); // selected rows order nums (unchanged)
  const [reprintInput, setReprintInput] = useState<string>(""); // free-text input for manual reprint
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isReprintEnable, setIsReprintEnable] = useState<boolean>(false);
  const [exportFile, setExportFile] = useState(false);
  const [open, setOpen] = useState(false);
  const [openaquity, setOpenaquity] = useState(false);
  const [label, setLabel] = useState<any>("");
  const [vendorReceiptModal, setVendorReceiptModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const { hasPermission } = useRolePermission();
  const textAreaRef = useRef<any>(null);
  const scanUPCTextAreaRef = useRef<any>(null);

  // Ref to prevent duplicate dispatches within a short time window
  const lastFetchAtRef = useRef<number | null>(null);

  // Date range picker defaults to last 90 days, but user can override with backDays or specific dates
  let memoizeStartDate = dayjs().subtract(90, "days").format("YYYY-MM-DD");
  let memoizeEndDate = dayjs().format("YYYY-MM-DD");
  const [dateRange, setDateRange] = useState({
    startDate: memoizeStartDate,
    endDate: memoizeEndDate,
  });

  const filterValues = filterColumns({
    selectedRow,
    setSelectedRow,
    notFoundRow,
    setNotFoundRow,
    rowSelection,
    setRowSelection,
  });

  useEffect(() => {
    fetchVendorList().then((res: any[]) => {
      const sortedVendor = res?.sort((a: any, b: any) => {
        return a.VendorName.localeCompare(b.VendorName, "en", {
          numeric: true,
        });
      });
      setVendors(sortedVendor);
    });

    setBackDays([
      { ID: "7", Name: "Over 7 Days Old" },
      { ID: "14", Name: "Over 14 Days Old" },
      { ID: "21", Name: "Over 21 Days Old" },
    ]);
  }, []);

  // Load on mount and when vendor or backDays change
  useEffect(() => {
    onLoad();
  }, [vendorId, backDaysId]);

  const handleSearch = () => {
    onLoad();
  };

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );
    const selectedRows = selectedIds.map(
      (index) => filterValues.data[parseInt(index)]
    );
    let newRows = notFoundRow.filter(
      (notFoundSelectedRow: SPECIAL_ORDER_QUEUE_DATA_TYPE) =>
        !selectedRows.find(
          (row: SPECIAL_ORDER_QUEUE_DATA_TYPE) =>
            row.OrderNum == notFoundSelectedRow.OrderNum
        )
    );
    setNotFoundRow(newRows);
    setSelectedRow(selectedRows);
  }, [rowSelection]);

  const onLoad = () => {
    setSelectedRow([]);
    setRowSelection({});
    setFilteredData([]);
    setLoader(true);
    let payload: any = {
      vendorId: vendorId == undefined ? 0 : vendorId,
      fromDate: dateRange?.startDate
        ? dayjs(dateRange.startDate).format("YYYY-MM-DD")
        : undefined,
      toDate: dateRange?.endDate
        ? dayjs(dateRange.endDate).format("YYYY-MM-DD")
        : undefined,
      backDays: backDaysId == undefined ? 0 : backDaysId,
      offset: 0,
      limit: 10000,
      currentUser: userName,
      UPC : "", // keep UPC empty for initial load, it's only used for filtering after load
    };

    dispatch(fetchSpecialOrders(payload));
    setLoader(false);
  };

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (k) => rowSelection[k]
    );
    const selectedRows = selectedIds.map(
      (index) => filterValues.data[parseInt(index)]
    );
    setSelectedRow(selectedRows);
    // keep a simple list of selected OrderNums for printing/reprint actions
    setOrderList(selectedRows.map((r: any) => r?.OrderNum).filter(Boolean));
  }, [rowSelection, filterValues.data]);

  const changeVendor = (id: string) => {
    setVendorId(id);
  };

  const changeBackDays = (id: any) => {
    setBackDaysId(id);
    // clear any From/To date entries when a backDays option is selected or cleared
    // use empty strings to keep type as string and avoid TS errors
    setDateRange({ startDate: "", endDate: "" });
  };

  const closeModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    setExportFile(true);
  };

  const startDateChange = (date: any) => {
    if (!date) {
      setDateRange({ endDate: date, startDate: date });
      // setBackDaysId("");
    } else {
      memoizeStartDate = date;
      setDateRange({ ...dateRange, startDate: date });
      //setBackDaysId("");
    }
  };
  const endDateChange = (date: any) => {
    memoizeEndDate = date;
    setDateRange({ ...dateRange, endDate: date });
  };
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);

  const printSelected = async () => {
	// collect selected order numbers from selectedRow
	if (!selectedRow?.length) {
		showErrorNotification("Please select at least one order number");
		return;
	}
	setLoader(true);
	const odNum = selectedRow.map((r: any) => (r?.OrderNum ?? "").toString()).filter(Boolean);
	const invalid = odNum.filter((n: any) => isNaN(Number(n)));
	if (invalid.length) {
		const msg = `The following order numbers appear to be invalid, please check them and try again: ${invalid.join(", ")}`;
		setErrorMessage(msg);
		showErrorNotification(msg);
		setLoader(false);
		return;
	}
	try {
		const res: any = await reprintLabelsReport({
			currentUser: userName,
			OrderNumbers: odNum.map(Number),
		});
		if (res?.length > 0) {
			setIsReprintEnable(true);
			setStream(res);
			setRowSelection({});
			setSelectedRow([]);
		} else {
		 showErrorNotification("No labels returned for the selected orders.");
		}
	} catch (err: any) {
		const msg = err?.response?.data?.error?.messages?.[0] || "Error";
		setErrorMessage(msg);
		showErrorNotification(msg);
	} finally {
		setLoader(false);
	}
};

  const openReprint = () => {
    setIsScanUPCMode(false); // ensure mode is for reprint
    setReprintModal(true);
  };

  const openScanUPC = () => {
    setIsScanUPCMode(true); // set mode for scan upc
    setReprintModal(true);
  };

  const onPrint = async () => {
    setLoader(true);
    try {
      let records: any[] = [];
      if (label === "all") {
        records = filterValues.data;
      } else {
        records = selectedRow;
      }
      const payload = {
        IdList: records.map((row: any) => row.Id),
        IsPrinted: true,
        currentUser: userName,
      };
      const res = await updatePrintLabel(payload);
      setStream("");
      setSelectedRow([]);
      setRowSelection({});
      setNotFoundRow([]);
      showSuccessNotification(res);
    } catch (error) {
      showErrorNotification(
        "Print label request was not successful. Please try again"
      );
    }
    setLoader(false);
  };

  const acuityClickHandler = async () => {
    setLoader(true);
    try {
      const acuityData: any = [];
      selectedRow.forEach(
        ({
          OfficeNum,
          OrderNum,
          LocationStatus,
        }: SPECIAL_ORDER_QUEUE_DATA_TYPE) => {
          acuityData.push({
            OfficeNumber: OfficeNum,
            OrderNumber: OrderNum,
            LocationStatus: LocationStatus,
            ModifiedBy: userName,
            IsFound: true,
          });
        }
      );
      notFoundRow.forEach(
        ({
          OfficeNum,
          OrderNum,
          LocationStatus,
        }: SPECIAL_ORDER_QUEUE_DATA_TYPE) => {
          acuityData.push({
            OfficeNumber: OfficeNum,
            OrderNumber: OrderNum,
            LocationStatus: LocationStatus,
            ModifiedBy: userName,
            IsFound: false,
          });
        }
      );

      const res = await updateAcuityStatus(Object.values(acuityData));
      if (res) {
        showSuccessNotification(
          "Successfully updated the order status in Acuity."
        );
        setSelectedRow([]);
        setRowSelection({});
        setNotFoundRow([]);
        setOpenaquity(false);
        setLoader(false);
        // Refresh grid to remove updated items
        onLoad();
      } else {
        showErrorNotification(
          "Error occurred while updating the order status in Acuity."
        );
        setLoader(false);
        setOpenaquity(false);
      }
    } catch (err) {
      showErrorNotification(
        "Error occurred while updating the order status in Acuity."
      );
      setLoader(false);
      setOpen(false);
    }
  };

  const exportExcelFile = async () => {
    setConfirmLoading(true);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("My Sheet");
    //sheet.properties.defaultRowHeight = 25;

    sheet.columns = SPECIAL_ORDER_QUEUE_EXCEL_COLUMNS;
    const data =
      filteredData?.map((row: any) => row.original).length > 0
        ? filteredData?.map((row: any) => row.original)
        : filterValues?.data;
    try {
      if (data?.length > 0) {
        setOpen(false);
        setConfirmLoading(false);
        let color;
        const promise = Promise.all(
          data?.map(async (item: any, index: number) => {
            const rowNumber = index + 2;
            sheet.addRow({
              OfficeNum: item.OfficeNum,
              OrderNum: item.OrderNum,
              OrderDate:
                item.OrderDate !== null
                  ? dayjs(item.OrderDate).format("YYYY-MM-DD")
                  : "",
              FrameCollection: item.FrameCollection,
              VendorName: item.VendorName,
              VendorOrderNumber: item.VendorOrderNumber,
              VendorReceiptNumber: item.VendorReceiptNumber,
              ReceiptNumber: item.ReceiptNumber,
              FrameName: item.FrameName,
              UPCCode: item.UPCCode,
              ItemStatus: item.ItemStatus,
              LocationStatus: item.LocationStatus,
            });
          })
        );

        promise.then(() => {
          workbook.xlsx.writeBuffer().then(function (res: BlobPart) {
            const blob = new Blob([res], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = "Special-Order-Queue-Report.csv";
            anchor.click();
            window.URL.revokeObjectURL(url);
          });
          showSuccessNotification("Excel downloaded successfully");
        });
      } else {
        setOpen(false);
        setConfirmLoading(false);
        showErrorNotification("No data available for the filter criteria");
      }
    } catch (error: any) {
      setOpen(false);
      setConfirmLoading(false);
      showErrorNotification(error.response.data.error.messages[0]);
    }
  };

  const printLabelHandler = async (printType: any) => {
      setLoader(true);
      let records = [];
      setLabel(printType);
      if (printType === "all") {
        records = filterValues.data;
      } else {
        records = selectedRow;
      }
      try {
        const payload = {
          IdList: records.map((row: any) => row.Id),
          IsPrinted: false,
          // PickerId: pickerId,
          currentUser: userName,
        };
        const res = await updatePrintLabel(payload);
        if (res) {
          const reportPayload = {
            reportName: "LabelFormNTEST",
            parameters: {
              //Picker: pickerId,
            },
          };
          const printRes = await printLabelReport(reportPayload);
          if (printRes) {
            setStream(printRes);
          }
        }
      } catch (error) {
        showErrorNotification(
          undefined,
          "Print label request was not successful. Please try again"
        );
      }
      setLoader(false);
    };

    const handleOpenAcuity = () => {
      // Validate selected rows: ReceiptNumber must be > 0
      if (selectedRow?.length) {
        const invalidRows = selectedRow.filter(
          (r: any) => !(Number(r?.ReceiptNumber) > 0)
        );
        if (invalidRows.length) {
          const orders = invalidRows
            .map((r: any) => r?.OrderNum)
            .filter((v: any) => v !== undefined && v !== null && v !== "");
          const msg = `Unable to update the order status in Acuity. A vendor receipt has not been created for the following order numbers ${orders.join(
            ", "
          )}`;
          showErrorNotification(msg);
          return;
        }
      }
      setOpenaquity(true);
    };
  
  const [filteredUPCList, setFilteredUPCList] = useState<string[]>([]); // NEW: store UPCs for filtering

  const handleReprint = async () => {
    if (!reprintInput?.length) {
      showErrorNotification(isScanUPCMode ? "Please scan at least one UPC" : "Please enter atleast one order number");
      return;
    }
    setLoader(true);
    let odNum: any[] = [];
    let strExist: any[] = [];
    if (isScanUPCMode) {
    // UPCs from Scan UPC popup: replace newlines with commas
    const upcString = reprintInput
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .join(',');

    let payload: any = {
      vendorId: vendorId == undefined ? 0 : vendorId,
      fromDate: dateRange?.startDate
        ? dayjs(dateRange.startDate).format("YYYY-MM-DD")
        : undefined,
      toDate: dateRange?.endDate
        ? dayjs(dateRange.endDate).format("YYYY-MM-DD")
        : undefined,
      backDays: backDaysId == undefined ? 0 : backDaysId,
      offset: 0,
      limit: 10000,
      currentUser: userName,
      UPC: upcString,
    };

    dispatch(fetchSpecialOrders(payload));
    setLoader(false);
    setReprintModal(false);
    setReprintInput("");
    setIsScanUPCMode(false);
    setErrorMessage("");
    return;
  } else {
      // Reprint mode: treat as order numbers
      odNum = convertStrToArr(reprintInput);
      strExist = odNum?.filter((item: any) => isNaN(item));
      if (odNum?.map(Number)?.includes(NaN)) {
        const msg = `The following order numbers appear to be invalid, please check them and try again: ${strExist?.join(
          ", "
        )}`;
        setErrorMessage(msg);
        showErrorNotification(msg);
        setLoader(false);
        return;
      }
    }
    try {
      // For demonstration, use the same reprintLabelsReport for both modes.
      // In real use, you may want to call a different API for UPCs.
      const res: any = await reprintLabelsReport({
        currentUser: userName,
        OrderNumbers: isScanUPCMode ? odNum : odNum,
      });
      if (res?.length > 0) {
        setIsReprintEnable(true);
        setReprintModal(false);
        setStream(res);
        setReprintInput("");
        setLoader(false);
        setRowSelection({});
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error?.messages?.[0] || "Error";
      setErrorMessage(msg);
      showErrorNotification(msg);
      setLoader(false);
    }
  };

  // Reset grid filter if user changes filter/search/vendor/backDays
  useEffect(() => {
    if (filteredUPCList.length > 0) {
      setFilteredData(
        filterValues.data.filter((row: any) => filteredUPCList.includes(row.UPCCode))
      );
    } else {
      setFilteredData([]);
    }
  }, [filterValues.data, filteredUPCList]);

  const handleCancelVendorReceipt = () => {
    setVendorReceiptModal(false);
    setInvoiceNumber('');
    setPoNumber('');
  }
  const handleMarkAsReceived = async () => {
    if (!invoiceNumber.trim()) {
      showErrorNotification("Invoice Number is required.");
      return;
    }
    if (invoiceNumber.length > 100) {
      showErrorNotification("Invoice Number cannot exceed 100 characters.");
      return;
    }
    if (poNumber.length > 20) {
      showErrorNotification("PO Number cannot exceed 20 characters.");
      return;
    }
    const vendorId = vendors.find((v: any) => v.VendorName === selectedRow[0]?.VendorName)?.ID || "0";
    const payload = {
      vendorId: vendorId.toString(),
      invoiceNumber: invoiceNumber,
      poNumber: poNumber,
      UserName: userName,
      itemdetails: selectedRow.map((row: any) => ({
        upc: row.UPCCode,
        patientOrderNumber: row.OrderNum,
        vendorOrderNumber: row.VendorOrderNumber,
        quantity: 1,
      })),
    };
    try {
      // use project's api client and API constant instead of hardcoded URL
      const response: any = await api.post(SPECIAL_ORDER_QUEUE_URL.VENDOR_RECEIPT, payload);
      // response handling follows existing UI behavior
      showSuccessNotification("Items marked as received successfully.");
      setVendorReceiptModal(false);
      setInvoiceNumber('');
      setPoNumber('');
      // refresh grid
      onLoad();
    } catch (error) {
      showErrorNotification("An error occurred while marking items as received.");
      setVendorReceiptModal(false);
      setInvoiceNumber('');
      setPoNumber('');
    }
  };

  const { startDate, endDate }: any = dateRange;
  return (
    <Spin tip="Loading..." spinning={loader}>
      <div className="frame-pick-wrapper">
        <Divider />
        <Breadcrumb>
          <span>Special Order Queue</span>
        </Breadcrumb>

        <TableFilter>
          <div className="form align-items-end">
            <Label label="Select Vendor">
              <Select
                placeholder="Select"
                disabled={loader}
                showSearch
                value={vendorId}
                style={{ width: 120, height: 30 }}
                optionFilterProp="children"
                onChange={(value: string) => {
                  changeVendor(value);
                }}
                size="small"
                allowClear
              >
                {vendors?.map(({ ID, VendorName }: any) => {
                  return (
                    <Select.Option key={ID} value={ID}>
                      {VendorName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Label>
            <Label label={"From Date"}>
              <DatePicker
                value={startDate && dayjs(startDate)}
                disabledDate={(d: any) =>
                  !d ||
                  d.isAfter(dayjs()) ||
                  dayjs().add(-3650, "days") >= d ||
                  dayjs(endDate)?.isBefore(d)
                }
                placeholder="Start Date"
                onChange={startDateChange}
              />
            </Label>
            <Label label={"To Date"}>
              <DatePicker
                value={endDate && dayjs(endDate)}
                disabledDate={(d: any) =>
                  !d ||
                  d.isAfter(dayjs()) ||
                  d.isBefore(startDate) ||
                  dayjs(startDate)?.isAfter(d)
                }
                placeholder="End Date"
                onChange={endDateChange}
              />
            </Label>
            <div style={{ display: "flex" }}>
              <Button
                htmlType="submit"
                size="middle"
                onClick={handleSearch}
                style={{ marginLeft: 10 }}
              >
                Search
              </Button>
            </div>
            <Label label="Select Days">
              <Select
                placeholder="Select"
                disabled={loader}
                showSearch
                value={backDaysId}
                style={{ width: 130, height: 30 }}
                optionFilterProp="children"
                onChange={(value: any) => {
                  changeBackDays(value);
                }}
                size="small"
                allowClear
              >
                {backDays?.map(({ ID, Name }: any) => {
                  return (
                    <Select.Option key={ID} value={ID}>
                      {Name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Label>
            <div style={{ display: "flex", gap: 8 }}>
              {hasPermission("specialorderqueue_update") && (
                <Button
                  size="middle"
                  onClick={() => handleOpenAcuity()}
                  disabled={
                    loader || (!selectedRow.length && !notFoundRow.length)
                  }
                >
                  Update Acuity
                </Button>
              )}
              <Button
                size="middle"
                disabled={!selectedIds.length}
                onClick={printSelected}
              >
                Print Selected Labels
              </Button>
              <Button
                size="middle"
                onClick={openReprint}
                disabled={loader}
              >
                Reprint Labels
              </Button>
              <Button
                size="middle"
                onClick={openScanUPC}
                disabled={loader}
              >
                Scan UPC
              </Button>
              <Button
                size="middle"
                onClick={() => {
                  if (selectedRow.length > 0 && selectedRow.every((row: any) => row.VendorName === selectedRow[0].VendorName)) {
                    setVendorReceiptModal(true);
                  } else {
                    showErrorNotification("Please select rows with the same vendor name.");
                  }
                }}
                disabled={!selectedIds.length}
              >
                Mark as Received
              </Button>
            </div>
          </div>
          <div
            className="filter-actions"
            style={{ paddingTop: "16px", paddingLeft: "10px" }}
          >
            <Tooltip title="Export to Excel" placement="bottom">
              <Button
                size="middle"
                icon={<DownloadOutlined />}
                onClick={closeModal}
                aria-label="Export to Excel"
              >
                {"Export"}
              </Button>
            </Tooltip>
          </div>
        </TableFilter>

        <GenericGrid
          columns={filterValues.columns}
          data={filteredUPCList.length > 0 ? filteredData : filterValues.data}
          enableRowSelection={true}
          onRowSelectionChange={setRowSelection}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          filterDisplayMode="popover"
          highlightFilterMatch={true}
          paginationDisplayMode="pages"
          paginationProps={{
            color: "primary",
            shape: "rounded",
            showRowsPerPage: true,
            variant: "outlined",
            rowsPerPageOptions: [10, 50, 100, 500, 1000, 5000],
          }}
          enableStickyHeader={true}
          enableStickyFooter={true}
          enableColumnPinning={true}
          enableColumnOrdering={false}
          columnPinning={{}}
          isLoading={filterValues.loading}
          filteredData={filteredData}
          setFilteredData={setFilteredData}
          muiTableBodyCellProps={{
            sx: {
              fontSize: "10px",
              textAlign: "left",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontSize: "10px",
              fontWeight: "800",
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              textAlign: "left",
              padding: "5px 10px",
            },
          }}
          muiTableBodyRowProps={({ row }: any) => ({
            sx: {
              "& td": {
                padding: "5px 10px",
              },
            },
          })}
        />

        <Modal
          title="Acuity Update Confirmation"
          okText="Ok"
          open={openaquity}
          centered
          confirmLoading={loader}
          okButtonProps={{ disabled: loader }}
          style={{ top: 20 }}
          onOk={() => acuityClickHandler()}
          onCancel={() => setOpenaquity(false)}
        >
          <span>Are you sure you want to Update Acuity?</span>
        </Modal>

        <Modal
          title="Excel export Confirmation"
          okText="Ok"
          open={exportFile}
          centered
          confirmLoading={loader}
          style={{ top: 20 }}
          onOk={exportExcelFile}
          onCancel={() => setExportFile(false)}
        >
          <span>Are you sure you want to export this data?</span>
        </Modal>

        {stream ? (
          <Modal
            open={true}
            onOk={() => setStream("")}
            onCancel={() => setStream("")}
            footer={null}
            width={1000}
            destroyOnClose
          >
            <PDFStream onPrint={isReprintEnable ? () => {} : onPrint} stream={stream} />
          </Modal>
        ) : null}

         <Modal
          title={isScanUPCMode ? "Scan UPCs" : "Reprint Order Numbers"}
          okText={isScanUPCMode ? "Submit" : "Print"}
          centered
          width={500}
          confirmLoading={loader}
          open={reprintModal}
          onOk={handleReprint}
          onCancel={() => {
            setReprintModal(false);
            setReprintInput("");
            setErrorMessage("");
            setIsScanUPCMode(false);
          }}
        >
          <span>
            <TextArea
              ref={scanUPCTextAreaRef}
              onChange={(e) => {
                let value = e.target.value;
                if (isScanUPCMode) {
                  // Replace commas, semicolons, and spaces with newlines, and remove duplicate newlines
                  value = value.replace(/[,; ]+/g, '\n').replace(/\n{2,}/g, '\n');
                }
                setReprintInput(value);
                setErrorMessage("");
                // Move cursor to end after change
                setTimeout(() => {
                  if (scanUPCTextAreaRef.current) {
                    const textarea = scanUPCTextAreaRef.current.resizableTextArea.textArea;
                    textarea.selectionStart = textarea.value.length;
                    textarea.selectionEnd = textarea.value.length;
                  }
                }, 0);
              }}
              onKeyDown={(e) => {
                if (isScanUPCMode && e.key === "Enter") {
                  // Move cursor to new line after scan
                  setTimeout(() => {
                    if (scanUPCTextAreaRef.current) {
                      const textarea = scanUPCTextAreaRef.current.resizableTextArea.textArea;
                      textarea.selectionStart = textarea.value.length;
                      textarea.selectionEnd = textarea.value.length;
                    }
                  }, 0);
                }
              }}
              onPaste={(e) => {
                if (isScanUPCMode) {
                  e.preventDefault();
                  const paste = e.clipboardData.getData('text');
                  // Split pasted text into lines
                  const upcs = paste.replace(/[,; ]+/g, '\n').replace(/\n{2,}/g, '\n');
                  const newValue = (reprintInput ? reprintInput + '\n' : '') + upcs;
                  setReprintInput(newValue);
                  setErrorMessage("");
                  setTimeout(() => {
                    if (scanUPCTextAreaRef.current) {
                      const textarea = scanUPCTextAreaRef.current.resizableTextArea.textArea;
                      textarea.selectionStart = textarea.value.length;
                      textarea.selectionEnd = textarea.value.length;
                    }
                  }, 0);
                }
              }}
              value={reprintInput}
              rows={9}
              placeholder={isScanUPCMode ? "Scan or paste UPCs, one per line" : undefined}
              autoFocus={isScanUPCMode}
            ></TextArea>
            <p style={{ color: "red", fontSize: "10px" }}>{errorMessage}</p>
          </span>
        </Modal>

        <Modal
          title={<div style={{ textAlign: 'center', backgroundColor: '#f0f0f0', padding: '10px' }}>Vendor Receipt</div>}
          open={vendorReceiptModal}
          onCancel={handleCancelVendorReceipt}
          footer={
            <div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '10px' }}>
                <Button onClick={handleCancelVendorReceipt}>
                  Cancel
                </Button>
                <Button type="primary" onClick={handleMarkAsReceived}>
                  Mark As Received
                </Button>
              </div>
              <div style={{ height: '20px' }}></div>
            </div>
          }
          width={800}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <span style={{ fontWeight: 'bold', marginLeft: '10px' }}>Vendor :</span>
            <span>{selectedRow[0]?.VendorName}</span>
            <span style={{ fontWeight: 'bold' }}>Invoice Number</span>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Enter Invoice Number"
              style={{ width: '200px' }}
              maxLength={100}
            />
            <span style={{ fontWeight: 'bold' }}>PO Number</span>
            <Input
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="Enter PO Number"
              style={{ width: '200px' }}
              maxLength={20}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Order Number</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Vendor Order Number</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>UPC Code</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {selectedRow.map((row: any) => (
                  <tr key={row.OrderNum}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.OrderNum}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.VendorOrderNumber}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.UPCCode}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      </div>

    </Spin>
  );
}

export default SpecialOrderQueue;
